import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useLanguage } from '../hooks/useLanguage'
import Layout from '../components/layout/Layout'

interface PaymentErrorPageProps {
  trancheType?: 'first' | 'second'
  tripName?: string
  amount?: number
  tripId?: string
  errorMessage?: string
  onClose?: () => void
}

export const PaymentErrorPage: React.FC<PaymentErrorPageProps> = ({
  trancheType,
  tripName,
  amount,
  tripId,
  errorMessage,
  onClose
}) => {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Get parameters from URL if not provided as props
  const urlTrancheType = searchParams.get('tranche') as 'first' | 'second' | null
  const urlTripName = searchParams.get('tripName')
  const urlAmount = searchParams.get('amount')
  const urlTripId = searchParams.get('tripId')
  const urlErrorMessage = searchParams.get('error')

  // Use props or URL parameters
  const finalTrancheType = trancheType || urlTrancheType
  const finalTripName = tripName || urlTripName
  const finalAmount = amount || (urlAmount ? parseFloat(urlAmount) : undefined)
  const finalTripId = tripId || urlTripId
  const finalErrorMessage = errorMessage || urlErrorMessage

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(language === 'it' ? 'it-IT' : language === 'nl' ? 'nl-NL' : 'en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const handleRetryPayment = () => {
    if (finalTripId) {
      navigate(`/trip/${finalTripId}`)
    } else {
      navigate('/')
    }
  }

  const handleViewBookings = () => {
    navigate('/account')
  }

  const handleContactSupport = () => {
    navigate('/contact')
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      navigate('/')
    }
  }

  return (
    <Layout>
      <div className="flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('payment.paymentFailed')}
          </h1>
          
          <p className="text-gray-600 mb-6">
            {t('payment.paymentFailedMessage')}
          </p>

          {finalTripName && finalAmount && finalTrancheType && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">{t('payment.paymentDetails')}</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  <strong>{t('payment.trip')}:</strong> {finalTripName}
                </p>
                <p>
                  <strong>{t('payment.paymentType')}:</strong> {finalTrancheType === 'first' ? t('payment.depositLabel') : t('payment.balanceLabel')}
                </p>
                <p>
                  <strong>{t('payment.amount')}:</strong> {formatCurrency(finalAmount)}
                </p>
              </div>
            </div>
          )}

          {finalErrorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong>{t('payment.errorDetails')}:</strong> {finalErrorMessage}
              </p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">{t('payment.whatToDo')}</h4>
            <ul className="text-sm text-blue-800 space-y-1 text-left">
              <li>• {t('payment.checkCardDetails')}</li>
              <li>• {t('payment.ensureSufficientFunds')}</li>
              <li>• {t('payment.tryDifferentCard')}</li>
              <li>• {t('payment.contactSupportIfNeeded')}</li>
            </ul>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleRetryPayment}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('payment.retryPayment')}
            </button>
            
            <button
              onClick={handleViewBookings}
              className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors"
            >
              {t('payment.viewBookings')}
            </button>
            
            <button
              onClick={handleContactSupport}
              className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('payment.contactSupport')}
            </button>
            
            <button
              onClick={handleClose}
              className="w-full bg-transparent text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('payment.backToHome')}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default PaymentErrorPage 